import React, { useCallback, useEffect, useState } from "react";
import { FileText, Loader2, X } from "lucide-react";
import api, { apiErrorMessage } from "../../lib/api";
import { FormattedTextarea } from "./FormattedText";
import { linksFromText, linksToText } from "../../lib/courseLibrary";
import { uploadErrorText, uploadFile } from "../../lib/portalUpload";
import { Chip, fieldCls, primaryBtn, secondaryBtn } from "./PortalShell";

const TONES = { pending: "warn", approved: "good", rejected: "late", withdrawn: "neutral" };
const TZ = "Europe/Bucharest";
const day = (iso) => (iso ? new Intl.DateTimeFormat("ro-RO", { timeZone: TZ, day: "numeric", month: "short" })
  .format(new Date(iso)) : "");

/**
 * "Cere o modificare" (28.10.2026, decision 12): the trainer writes the
 * requirement as they want it for their group; the FPED team approves or
 * refuses it. Until then, the group keeps the library's.
 */
export default function ChangeRequest({ activity }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(activity.description || "");
  const [links, setLinks] = useState(linksToText(activity.links));
  const [attached, setAttached] = useState([]);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const load = useCallback(() => {
    api.get(`/portal/activities/${activity.id}/change-requests`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [activity.id]);
  useEffect(() => { load(); }, [load]);

  const pending = (data?.requests || []).find((r) => r.status === "pending");

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const row = await uploadFile(`/portal/activities/${activity.id}/change-requests/uploads`, file);
      setAttached((list) => [...list, row]);
    } catch (err) {
      setMessage({ tone: "bad", text: uploadErrorText(err, apiErrorMessage) });
    } finally {
      setBusy(false);
    }
  };
  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/portal/activities/${activity.id}/change-requests`,
        { description: text, links: linksFromText(links), files: attached.map((f) => f.id), reason });
      setMessage({ tone: "good", text: "Cererea a plecat la echipa FPED. Până la răspuns, grupa are cerința din bibliotecă." });
      setOpen(false);
      setAttached([]);
      setReason("");
      load();
    } catch (err) {
      setMessage({ tone: "bad", text: apiErrorMessage(err, "Nu am putut trimite cererea.") });
    } finally {
      setBusy(false);
    }
  };
  const withdraw = async (r) => {
    setBusy(true);
    try { await api.post(`/portal/change-requests/${r.id}/withdraw`); load(); }
    catch (err) { setMessage({ tone: "bad", text: apiErrorMessage(err, "Nu am putut retrage cererea.") }); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid gap-3 rounded-2xl border border-[#E5E7EB] bg-white p-5" data-testid="portal-change-request">
      <div>
        <p className="text-sm font-semibold text-[#373A45]">Cerința temei</p>
        <p className="text-xs text-[#6B7280]">
          {data?.own_version
            ? "Grupa are varianta ta a cerinței, aprobată de echipa FPED."
            : "E cea din biblioteca cursului, la fel în toate grupele. Dacă vrei alta pentru grupa ta, cere-o echipei FPED."}
        </p>
      </div>

      {(data?.requests || []).length > 0 && (
        <ul className="grid gap-2">
          {data.requests.map((r) => (
            <li key={r.id} className="rounded-lg bg-[#F7F5F9] px-3 py-2 text-sm" data-testid="portal-change-request-row">
              <div className="flex flex-wrap items-center gap-2">
                <Chip tone={TONES[r.status]}>{r.status_label}</Chip>
                <span className="text-xs text-[#6B7280]">cerută pe {day(r.requested_at)}</span>
                {r.status === "pending" && (
                  <button type="button" disabled={busy} onClick={() => withdraw(r)}
                    className="ml-auto text-xs font-semibold text-[#6B7280] hover:text-[#a33a32]">Retrage</button>
                )}
              </div>
              {r.decision_reason && <p className="mt-1 text-xs text-[#373A45]">Răspunsul echipei: {r.decision_reason}</p>}
            </li>
          ))}
        </ul>
      )}

      {!open && !pending && (
        <div>
          <button type="button" className={secondaryBtn} onClick={() => { setOpen(true); setMessage(null); }}
            data-testid="portal-change-request-open">
            Cere o modificare
          </button>
        </div>
      )}
      {open && (
        <div className="grid gap-3">
          <label className="grid gap-1 text-sm font-semibold text-[#373A45]">
            Cerința, așa cum o vrei pentru grupa ta
            <FormattedTextarea value={text} onChange={setText} rows={8} maxLength={8000} className={fieldCls}
              data-testid="portal-change-request-text" />
          </label>
          <label className="grid gap-1 text-sm font-semibold text-[#373A45]">
            Linkuri (opțional)
            <textarea value={links} onChange={(e) => setLinks(e.target.value)} rows={2} className={fieldCls}
              placeholder="Câte unul pe rând. Poți pune un nume înainte: Model | https://…" />
          </label>
          <div className="grid gap-1 text-sm">
            <span className="font-semibold text-[#373A45]">Fișiere (opțional)</span>
            {attached.length > 0 && (
              <ul className="grid gap-1">
                {attached.map((f) => (
                  <li key={f.id} className="flex items-center gap-2 text-sm">
                    <FileText size={14} /> {f.name}
                    <button type="button" aria-label={`Scoate ${f.name}`} onClick={() => setAttached((l) => l.filter((x) => x.id !== f.id))}
                      className="text-[#9CA3AF] hover:text-[#a33a32]"><X size={13} /></button>
                  </li>
                ))}
              </ul>
            )}
            <label className={`${secondaryBtn} w-fit cursor-pointer`}>
              {busy ? <Loader2 size={13} className="animate-spin" /> : null} Atașează un fișier
              <input type="file" className="sr-only" disabled={busy} onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
            </label>
          </div>
          <label className="grid gap-1 text-sm font-semibold text-[#373A45]">
            De ce vrei schimbarea
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={1000} className={fieldCls}
              placeholder="De exemplu: grupa e de învățământ primar, iar cerința e gândită pentru gimnaziu."
              data-testid="portal-change-request-reason" />
          </label>
          <div className="flex flex-wrap gap-2">
            <button type="button" disabled={busy || !text.trim() || !reason.trim()} className={primaryBtn} onClick={send}
              data-testid="portal-change-request-send">
              Trimite cererea
            </button>
            <button type="button" disabled={busy} className={secondaryBtn} onClick={() => setOpen(false)}>Renunță</button>
          </div>
        </div>
      )}
      {message && (
        <p className={`text-sm ${message.tone === "bad" ? "text-[#c0483f]" : "text-[#3c7457]"}`} role="status">{message.text}</p>
      )}
    </div>
  );
}
